import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
} from "@ondit/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access";
import { ApiError, post, request } from "~/core/api";
import { pick, useI18n } from "~/core/i18n";

/**
 * The brand events an Agent workflow can start on, by the name the server
 * uses. Each screen passes the one its rows are, and the run gets the payload
 * the event itself would have carried.
 */
export type AgentEvent = "gap_found" | "opportunities_ready";

interface Target {
	brandId: string;
	event: AgentEvent;
	objectId: string;
}

const COPY = {
	action: ["Run with Agent", "交给 Agent 运行"],
	title: ["Run a workflow", "运行工作流"],
	workflow: ["Workflow", "工作流"],
	choose: ["Choose a workflow", "选择工作流"],
	run: ["Run", "运行"],
	running: ["Starting…", "正在启动…"],
	cancel: ["Cancel", "取消"],
	close: ["Close", "关闭"],
	started: ["Run started", "已开始运行"],
	none: [
		"No deployed workflow starts on this event. Add an Ondit Event trigger to a workflow and deploy it.",
		"还没有已部署的工作流由此事件启动。请在工作流中添加 Ondit 事件触发器并部署。",
	],
	open: ["Open Agent", "打开 Agent"],
	noAgent: ["Your plan does not include the Agent", "当前套餐不包含 Agent"],
} as const satisfies Record<string, readonly [string, string]>;

/** Whether this person may start a run here, and the sentence when not. */
export function useAgentRun(): { allowed: boolean; why: string | undefined } {
	const { locale } = useI18n();
	const { agent, editor, why } = useAccess();
	if (!agent) return { allowed: false, why: pick(locale, COPY.noAgent) };
	if (!editor) return { allowed: false, why: why.editor };
	return { allowed: true, why: undefined };
}

/** The label for a control that opens {@link RunWithAgentModal}. */
export function useRunWithAgentLabel(): string {
	const { locale } = useI18n();
	return pick(locale, COPY.action);
}

/** A button and its dialog, for a screen with one object. */
export function RunWithAgentButton(props: Target) {
	const [open, setOpen] = useState(false);
	const { allowed, why } = useAgentRun();
	const label = useRunWithAgentLabel();
	return (
		<>
			<Button variant="ghost" type="button" disabled={!allowed} title={why} onClick={() => setOpen(true)}>
				{label}
			</Button>
			{open ? <RunWithAgentModal {...props} onClose={() => setOpen(false)} /> : null}
		</>
	);
}

/**
 * The brand's deployed workflows listening for this event, and a Run. Mounted
 * only while open, so the list is read when somebody asks for it.
 */
export function RunWithAgentModal({ brandId, event, objectId, onClose }: Target & { onClose: () => void }) {
	const { locale } = useI18n();
	const tr = (key: keyof typeof COPY) => pick(locale, COPY[key]);
	const { allowed } = useAgentRun();
	const [chosen, setChosen] = useState("");
	const list = useQuery({
		queryKey: ["agent-event-workflows", brandId, event],
		queryFn: () =>
			request<{ workflows: { id: string; name: string }[] }>(
				`/agent/brands/${brandId}/event-workflows?${new URLSearchParams({ event })}`,
			),
	});
	const workflows = list.data?.workflows ?? [];
	const workflow = chosen || (workflows.length === 1 ? workflows[0].id : "");
	const run = useMutation({
		mutationFn: () => post(`/agent/brands/${brandId}/event-workflows/run`, { workflowId: workflow, event, objectId }),
		onSuccess: () => {
			toast.success(tr("started"));
			onClose();
		},
		onError: (e) => toast.error(e instanceof ApiError ? e.localized(locale) : String(e)),
	});
	return (
		<ChipModal open onOpenChange={(open) => !open && onClose()} dismissDisabled={run.isPending} srTitle={tr("title")}>
			<ChipModalHeader onClose={onClose} closeAriaLabel={tr("close")}>
				{tr("title")}
			</ChipModalHeader>
			<ChipModalBody>
				<ChipModalField type="custom" title={tr("workflow")}>
					{list.isError ? (
						<p role="alert">{list.error instanceof ApiError ? list.error.localized(locale) : String(list.error)}</p>
					) : list.isSuccess && workflows.length === 0 ? (
						<p className="hint">
							{tr("none")} <Link to={`/w/${brandId}/agent/home`}>{tr("open")}</Link>
						</p>
					) : (
						<ChipSelect
							searchable
							aria-label={tr("workflow")}
							placeholder={tr("choose")}
							value={workflow}
							disabled={!list.isSuccess || run.isPending}
							onChange={setChosen}
							options={workflows.map((w) => ({ value: w.id, label: w.name }))}
						/>
					)}
				</ChipModalField>
			</ChipModalBody>
			<ChipModalFooter
				onCancel={onClose}
				cancelLabel={tr("cancel")}
				primaryAction={{
					label: run.isPending ? tr("running") : tr("run"),
					disabled: !allowed || !workflow || run.isPending,
					onClick: () => run.mutate(),
				}}
			/>
		</ChipModal>
	);
}
